import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsEvent, AnalyticsService } from '../../../core/services/analytics-service';
import { Component, Input, OnDestroy } from '@angular/core';
import { from, interval, Observable, of, Subject, throwError, timer } from 'rxjs';
import { catchError, filter, map, mapTo, mergeMap, retryWhen, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
// eslint-disable-next-line max-len
import { DialogService } from '../../../core/services/dialog.service';
import { PaymentService } from '../../../pay/services/payment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
// eslint-disable-next-line max-len
import {
  NewCustomerBillerInfoSnackBarComponent,
} from '../../../pay/components/new-customer-biller-info-snack-bar/new-customer-biller-info-snack-bar.component';
import {
  mapCustomerBillerStatusToSnackBarData,
  TIMEOUT_SNACKBAR_DATA,
} from '../../../pay/models/new-biller-snack-bar.model';
import { FakeProgress } from '../../utils/fake-progress';
import { PaymentErrorEnum } from '../../models/payment-error-enum.enum';
// eslint-disable-next-line max-len
import {
  NewCustomerBillerFromEmptyCartTimeoutDialogComponent,
} from '../new-customer-biller-from-empty-cart-timeout-dialog/new-customer-biller-from-empty-cart-timeout-dialog.component';
import { CustomerBiller } from '../../../billers/models/model';
import { CustomerBillerStoreService } from '../../../billers/services/customer-biller-store.service';
import { CustomerBillerStatus } from 'src/app/billers/models/customer-biller-status.model';
// eslint-disable-next-line max-len
import {
  RefreshConfirmationDialogComponent,
} from '../../../billers/components/refresh-confirmation-dialog/refresh-confirmation-dialog.component';
import { BillerDetailsSource } from '../../models/biller-details-source.enum';

const SNACKBAR_TIMEOUT_MS = 1.5 * 60 * 1000;
const DIALOG_TIMEOUT_MS = 10 * 60 * 1000;
const SHORT_POLLING_INTERVAL_MS = 2 * 1000;
export const EMPTY_CART_NEW_BILLER_FOR_COUPON_PROCESS_PARAM = 'emptyCartNewBillerForCouponProcess';
export const NEW_CUSTOMER_BILLER_ID_PARAM = 'newCustomerBillerId';

@Component({
  standalone: false,
  selector: 'app-empty-cart',
  styleUrls: ['./empty-cart.component.scss'],
  templateUrl: './empty-cart.component.html',
})
export class EmptyCartComponent implements OnDestroy {
  @Input() public selectedCouponId?: string;

  public liabilitiesLoading = false;
  public fakeProgress = new FakeProgress(60000, 95);
  public customerBiller: CustomerBiller;
  public progressBarVisible = false;
  public CustomerBillerStatus = CustomerBillerStatus;

  private timeoutSnackbarShown = false;
  private timeoutDialogShown = false;
  private unsubscribe = new Subject();
  private stopPolling = new Subject();

  constructor(
    private analyticsService: AnalyticsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private customerBillerService: CustomerBillerStoreService,
    private customerBillerStoreService: CustomerBillerStoreService,
    private dialogService: DialogService,
    private paymentService: PaymentService,
    private snackbar: MatSnackBar,
  ) {
    this.activatedRoute.queryParamMap
      .pipe(
        map((queryParams) => queryParams.get(NEW_CUSTOMER_BILLER_ID_PARAM)),
        tap((newCustomerBillerId) => {
          if (!newCustomerBillerId) {
            this.liabilitiesLoading = false;
            this.progressBarVisible = false;
            this.fakeProgress.stop();
          }
        }),
        filter((newCustomerBillerId) => !!newCustomerBillerId),
        tap(() => {
          this.liabilitiesLoading = true;
          this.progressBarVisible = true;
        }),
        switchMap((newCustomerBillerId) => customerBillerService.getCustomerBiller(newCustomerBillerId)),
        tap((customerBiller) => this.fakeProgress.start(this.getElapsedTime(customerBiller))),
        switchMap((customerBiller) =>
          interval(SHORT_POLLING_INTERVAL_MS).pipe(mapTo(customerBiller.id), takeUntil(this.stopPolling)),
        ),
        switchMap((newCustomerBillerId) => customerBillerService.getCustomerBiller(newCustomerBillerId)),
        tap((customerBiller: CustomerBiller) => {
          this.customerBiller = customerBiller;
          if (
            !this.isSuccess(customerBiller) &&
            this.getElapsedTime(customerBiller) > SNACKBAR_TIMEOUT_MS &&
            !this.timeoutSnackbarShown
          ) {
            this.timeoutSnackbarShown = true;
            this.showTimeoutSnackbar();
          }

          if (
            !this.isSuccess(customerBiller) &&
            this.getElapsedTime(customerBiller) > DIALOG_TIMEOUT_MS &&
            !this.timeoutDialogShown
          ) {
            this.timeoutDialogShown = true;
            this.stopPolling.next(true);
            this.showTimeoutDialog();
          }
        }),
        filter((customerBiller) => this.isSuccess(customerBiller)),
        switchMap((customerBiller) => {
          this.fakeProgress.stop();
          if (customerBiller.liabilityCount > 0) {
            return this.createPaymentWithLiabilities();
          } else {
            this.showInfoSnackbar(customerBiller);
            return of(null);
          }
        }),
        tap(() => {
          this.liabilitiesLoading = false;
          this.progressBarVisible = false;
        }),
        retryWhen(this.retryOnTooManyRequests()),
        takeUntil(this.unsubscribe),
        take(1),
      )
      .subscribe();
  }

  private getElapsedTime(customerBiller: CustomerBiller) {
    return new Date().getTime() - new Date(customerBiller.userpanelAccess.updatedAt).getTime();
  }

  private isSuccess(customerBiller: CustomerBiller) {
    const successStatuses = [
      CustomerBillerStatus.INVALID,
      CustomerBillerStatus.USER_ACTION_NEEDED,
      CustomerBillerStatus.ENABLED,
    ];
    return successStatuses.includes(customerBiller.status);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
  }

  public refreshAccount = () =>
    this.customerBillerStoreService
      .refreshAccount(this.customerBiller.id)
      .pipe(tap(() => this.dialogService
        .open(RefreshConfirmationDialogComponent,
          { data: this.customerBiller, panelClass: ['padding-24'] })));

  public addBiller = (): Observable<any> => {
    this.analyticsService.trackEvent(new AnalyticsEvent('Add biller clicked on empty cart'));
    return from(
      this.router.navigate(['/billers/choose'], {
        queryParams: { [EMPTY_CART_NEW_BILLER_FOR_COUPON_PROCESS_PARAM]: true },
        queryParamsHandling: 'merge',
      }),
    );
  };

  public addLiability = (): Observable<any> => {
    this.analyticsService.trackEvent(new AnalyticsEvent('Add liability clicked on empty cart'));
    return from(this.router.navigate(['/liabilities/all']));
  };

  private showInfoSnackbar(customerBiller: CustomerBiller) {
    if (customerBiller.status === CustomerBillerStatus.ENABLED && customerBiller.liabilityCount === 0) {
      this.showNoPayableLiabilitiesSnackbar();
    } else if (customerBiller.status === CustomerBillerStatus.INVALID) {
      this.showInvalidCredentialsSnackbar();
    } else if (customerBiller.status === CustomerBillerStatus.USER_ACTION_NEEDED) {
      this.showUserActionNeededSnackbar(customerBiller);
    }
  }

  private showNoPayableLiabilitiesSnackbar() {
    this.snackbar
      .openFromComponent(NewCustomerBillerInfoSnackBarComponent, {
        data: mapCustomerBillerStatusToSnackBarData(CustomerBillerStatus.ENABLED, { hasPayableLiabilities: false }),
      })
      .afterDismissed()
      .pipe(filter((dismissed) => dismissed.dismissedByAction))
      .subscribe(() => this.addBiller());

    return of(null);
  }

  private showInvalidCredentialsSnackbar() {
    this.snackbar
      .openFromComponent(NewCustomerBillerInfoSnackBarComponent, {
        data: mapCustomerBillerStatusToSnackBarData(CustomerBillerStatus.INVALID),
      })
      .afterDismissed()
      .pipe(filter((dismissed) => dismissed.dismissedByAction))
      .subscribe(() =>
        this.router.navigate(['/billers', this.customerBiller.id, 'details', 'USERPANEL_SCRAPER'], {
          queryParamsHandling: 'merge',
          queryParams: {
            source: BillerDetailsSource.EMPTY_CART,
            [EMPTY_CART_NEW_BILLER_FOR_COUPON_PROCESS_PARAM]: true,
          },
        }),
      );
  }

  private showUserActionNeededSnackbar(customerBiller: CustomerBiller) {
    this.snackbar
      .openFromComponent(NewCustomerBillerInfoSnackBarComponent, {
        data: mapCustomerBillerStatusToSnackBarData(CustomerBillerStatus.USER_ACTION_NEEDED, {
          userActionNeeded: customerBiller.userpanelAccess.uanMessage.longMessage,
        }),
      })
      .afterDismissed()
      .pipe(filter((dismissed) => dismissed.dismissedByAction))
      .subscribe(() => this.addBiller());
  }

  private showTimeoutSnackbar() {
    this.snackbar
      .openFromComponent(NewCustomerBillerInfoSnackBarComponent, { data: TIMEOUT_SNACKBAR_DATA })
      .afterDismissed()
      .pipe(filter((dismissed) => dismissed.dismissedByAction))
      .subscribe(() => this.addBiller());
    return of(null);
  }

  private createPaymentWithLiabilities(): Observable<any> {
    return this.paymentService.createPaymentForCustomerBiller(this.customerBiller.id, this.selectedCouponId).pipe(
      tap(() => this.successSnackbar()),
      catchError((err) =>
        err?.error?.errorCode === PaymentErrorEnum.NO_SUGGESTED_LIABILITY_TO_PAY
          ? this.showNoPayableLiabilitiesSnackbar()
          : throwError(err),
      ),
    );
  }

  private successSnackbar() {
    // this.snackbar.openFromComponent(NewCustomerBillerInfoSnackBarComponent, {
    //   data: mapCustomerBillerStatusToSnackBarData(CustomerBillerStatus.ENABLED, { hasPayableLiabilities: true }),
    // });
  }

  private retryOnTooManyRequests() {
    return (errors) =>
      errors.pipe(
        mergeMap((err) => {
          if (err instanceof HttpErrorResponse && err.status === 429) {
            return timer(5000);
          } else {
            return throwError(err);
          }
        }),
      );
  }

  private showTimeoutDialog() {
    this.dialogService
      .open(NewCustomerBillerFromEmptyCartTimeoutDialogComponent, {
        maxWidth: '600px',
        disableClose: true,
      })
      .afterClosed()
      .subscribe(() => this.router.navigate(['.'], { replaceUrl: true, relativeTo: this.activatedRoute }));
  }
}
